import { ProtocolEditor } from '@/components/admin/protocoles/protocol-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchProtocol, fetchProtocolVersion } from '@/lib/api';
import { useConsumedCopyFlag } from '@/lib/copy-search';
import { protocolViewOf, readProtocolEditorSearch, viewSearch } from '@/lib/protocol-search';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'un protocole existant (refonte modulaire §4, §7.1; maquette
 * `editeur-protocole.html`, écran 3).
 *
 * ## Two queries, two shapes, two key prefixes (issue #16)
 *
 * `['protocol', email, id]` holds a `ProtocolDetail`; `['protocol-version',
 * email, id, versionId]` holds a `ProtocolVersion`. Different prefixes on
 * purpose: the trap of #16 is two readers agreeing on a key and disagreeing on
 * what is under it, and `useQuery` is generic per component so nothing would
 * catch the drift. Neither key overlaps `['protocols', email]`, which the list
 * fills with the card projection.
 *
 * ## « Voir » travels in the URL
 *
 * `?version=<id>` rather than component state, the régime of every filtered
 * screen here: a version somebody is reading is exactly what gets pasted into a
 * thread with « regarde la v3 ». The editor renders read-only while it is set
 * (§4), and `validateSearch` never throws - an unknown parameter degrades to
 * « no version » rather than to a blank screen.
 *
 * The SELECTED STEP does not travel with it, and that is deliberate: it is a
 * posture of the reader rather than a property of what is being read, and a
 * step id pasted into a thread resolves for nobody.
 *
 * ## The switch of #154 does: `?vue=lecture`
 *
 * « Conduite de l'entretien » or « Lecture pour l'analyste » (noyau.md §4.9,
 * N47) is a place somebody sends somebody else to - « Ouvrir le protocole »
 * from an experience's save box lands on the reading - so it travels like
 * `?tab=` on the experience's editor, read by `readProtocolEditorSearch` and
 * KEPT by every navigation of this screen: « Voir » a version from the reading
 * shows that version's reading. The switch REPLACES the entry rather than
 * pushing one, the régime of `?tab=`, and leaves the scroll where it was.
 *
 * ## `key` on the editor, and what it must NOT contain
 *
 * The editor initialises its draft state from `detail` ONCE, with no effect
 * copying a query into state. So a reload of a stale draft has to be a REMOUNT -
 * without one, the box would refetch and the stale draft would stay on screen,
 * which is the one thing that box exists to fix.
 *
 * The key is therefore the protocol's id plus a COUNTER this screen owns, and
 * that shape is the whole point. The obvious spelling - keying on
 * `draft.updatedAt` - is a defect: every autosave answers with a new stamp and
 * writes it into this query, so the key would change roughly once a second while
 * somebody types, remounting the field under their caret and - here worse than
 * on a criterion - throwing the selected step back to the first one on every
 * keystroke. A counter cannot be moved by a data update, only by the gesture
 * that asks for a reload; and the bump waits for the refetch to LAND, because
 * remounting before the fresh answer arrives would re-seed the editor from the
 * very draft it is escaping.
 */

export const Route = createFileRoute('/admin/protocoles/$protocolId')({
  validateSearch: readProtocolEditorSearch,
  component: ProtocolEditorPage,
});

function ProtocolEditorPage() {
  const { email } = useAdminSession();
  const { protocolId } = Route.useParams();
  const search = Route.useSearch();
  const { version, copie } = search;
  const view = protocolViewOf(search);
  const navigate = useNavigate();

  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const protocol = useQuery({
    queryKey: ['protocol', email, protocolId],
    queryFn: () => fetchProtocol(protocolId),
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['protocol-version', email, protocolId, version ?? null],
    queryFn: () => fetchProtocolVersion(protocolId, version ?? ''),
    enabled: version !== undefined,
    retry: false,
  });

  /*
    The flag is CONSUMED once the editor it describes has OPENED (§4.2).

    Two halves, and both matter. Left in the address bar it would re-select
    the name field at every later remount - and the counter just above is
    one: « Recharger le brouillon du serveur » bumps it. Dropped too EARLY it
    would never select anything at all: the editor reads the flag on ITS
    mount, and it does not mount until the entity has been read - so this
    waits for the entity, and React runs a child's effects before its
    parent's, which is what puts the selection before the drop.

    `replace`, because it is a hand-off between two screens and not a place
    anybody goes back to.
  */
  useConsumedCopyFlag(copie, protocol.data !== undefined, () =>
    navigate({
      to: '/admin/protocoles/$protocolId',
      params: { protocolId },
      // Rebuilt from what THIS route holds rather than reduced from the
      // previous search: TanStack types that argument as the union of every
      // route's shape, and a key OMITTED is the only spelling
      // `exactOptionalPropertyTypes` accepts.
      search: { ...(version === undefined ? {} : { version }), ...viewSearch(view) },
      replace: true,
    }),
  );

  const rejected = useRejectOn401(protocol.error);
  const missing = protocol.error instanceof ApiError && protocol.error.status === 404;

  return (
    <div className="space-y-6">
      <p className="text-gray-600 text-sm">
        <Link to="/admin/protocoles" className="text-bleu-nuit underline underline-offset-2">
          Protocoles
        </Link>
        <span className="mx-2">›</span>
        {protocol.data?.name ?? '...'}
      </p>

      {protocol.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement du protocole" />
        </div>
      ) : null}

      {missing ? (
        <p className="text-gray-700">
          Ce protocole n’existe pas.{' '}
          <Link to="/admin/protocoles" className="text-bleu-nuit underline underline-offset-2">
            Revenir à la bibliothèque
          </Link>
          .
        </p>
      ) : null}

      {protocol.isError && !missing && !rejected ? (
        <p className="text-gray-700">Ce protocole n’a pas pu être chargé.</p>
      ) : null}

      {protocol.data === undefined ? null : (
        <ProtocolEditor
          // The id and a counter, never the draft's stamp - see the head of this
          // file for what keying on the stamp would cost.
          key={`${protocol.data.id}-${reload}`}
          selectName={copie === true}
          detail={protocol.data}
          viewing={viewed.data ?? null}
          view={view}
          onSelectView={(next) =>
            void navigate({
              to: '/admin/protocoles/$protocolId',
              params: { protocolId },
              search: { ...(version === undefined ? {} : { version }), ...viewSearch(next) },
              replace: true,
              // The screen stays the same - only `?vue=` moves (issue #90's rule).
              resetScroll: false,
            })
          }
          onView={(summary) =>
            navigate({
              to: '/admin/protocoles/$protocolId',
              params: { protocolId },
              search: { version: summary.id, ...viewSearch(view) },
            })
          }
          onCloseView={() =>
            navigate({
              to: '/admin/protocoles/$protocolId',
              params: { protocolId },
              search: viewSearch(view),
            })
          }
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void protocol.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
